// src/pages/SocialReportsPage.js
//
// Reports page at /social/reports. No charting library is installed
// (checked package.json — only react-big-calendar / date-fns are present),
// so all "charts" below are rendered as plain tables / colored-width bar
// rows, matching the no-dependency convention used elsewhere in this app
// (e.g. StaffPerformanceReportPage.js).
//
// DATE FIELD CHOICE: the date-range filter is applied against `dueDate`
// for all the "by status / by client / by content type / by platform"
// breakdowns, because dueDate is the one field guaranteed to exist on
// every contentTask and represents "work that belongs to this period"
// regardless of where it currently sits in the workflow. The one
// exception is the "published vs target" metric per client, which counts
// a task as published-in-period using `publishedDate` instead, since that
// is the field that actually reflects when something went live — using
// dueDate there would miscount tasks that were published late/early
// outside the filtered range.
//
// Status -> color mapping (kept consistent with SocialDashboardPage.js /
// ContentCalendarPage.js):
//   New Request #9ca3af, Content Writing #60a5fa, Content Ready #3b82f6,
//   Design / Editing #a78bfa, Design Ready #8b5cf6, Internal Review #f59e0b,
//   Changes Requested #f97316, Ready for Client Approval #fbbf24,
//   Client Approval #eab308, Approved #14b8a6, Scheduled #6366f1,
//   Published #22c55e, Cancelled #6b7280

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { getStatusLabel, getPlatformLabel, getContentTypeLabel } from '../utils/socialEnumLabels';
import styles from '../styles/SocialReportsPage.module.css';

const TRANSLATIONS = {
  en: {
    pageTitle: 'Social Media Content Reports',
    startDate: 'Start Date:',
    endDate: 'End Date:',
    client: 'Client:',
    allClients: 'All Clients',
    platform: 'Platform:',
    allPlatforms: 'All Platforms',
    dateRangeError: 'End date must be on or after start date.',
    loading: 'Loading report data...',
    byStatus: 'By Status',
    byContentType: 'By Content Type',
    byPlatform: 'By Platform',
    overdueTasks: 'Overdue Tasks',
    noOverdueInRange: 'No overdue tasks in this range.',
    untitled: 'Untitled',
    noClient: 'No client',
    due: 'Due',
    byClient: 'By Client',
    noTasksInRange: 'No content tasks in this range.',
    colClient: 'Client',
    colTasksDueInRange: 'Tasks (Due in Range)',
    colPublishedInRange: 'Published (in Range)',
    colMonthlyTarget: 'Monthly Target (Total)',
    colProgress: 'Progress',
    noTargetSet: 'No target set',
  },
  ar: {
    pageTitle: 'تقارير محتوى وسائل التواصل الاجتماعي',
    startDate: 'تاريخ البداية:',
    endDate: 'تاريخ النهاية:',
    client: 'العميل:',
    allClients: 'جميع العملاء',
    platform: 'المنصة:',
    allPlatforms: 'جميع المنصات',
    dateRangeError: 'يجب أن يكون تاريخ النهاية بعد تاريخ البداية أو يساويه.',
    loading: 'جارٍ تحميل بيانات التقرير...',
    byStatus: 'حسب الحالة',
    byContentType: 'حسب نوع المحتوى',
    byPlatform: 'حسب المنصة',
    overdueTasks: 'المهام المتأخرة',
    noOverdueInRange: 'لا توجد مهام متأخرة في هذا النطاق.',
    untitled: 'بدون عنوان',
    noClient: 'بدون عميل',
    due: 'الاستحقاق',
    byClient: 'حسب العميل',
    noTasksInRange: 'لا توجد مهام محتوى في هذا النطاق.',
    colClient: 'العميل',
    colTasksDueInRange: 'المهام (المستحقة في النطاق)',
    colPublishedInRange: 'تم نشره (في النطاق)',
    colMonthlyTarget: 'الهدف الشهري (الإجمالي)',
    colProgress: 'التقدم',
    noTargetSet: 'لا يوجد هدف محدد',
  },
};

const STATUS_ORDER = [
  'New Request',
  'Content Writing',
  'Content Ready',
  'Design / Editing',
  'Design Ready',
  'Internal Review',
  'Changes Requested',
  'Ready for Client Approval',
  'Client Approval',
  'Approved',
  'Scheduled',
  'Published',
  'Cancelled',
];

const STATUS_COLORS = {
  'New Request': '#9ca3af',
  'Content Writing': '#60a5fa',
  'Content Ready': '#3b82f6',
  'Design / Editing': '#a78bfa',
  'Design Ready': '#8b5cf6',
  'Internal Review': '#f59e0b',
  'Changes Requested': '#f97316',
  'Ready for Client Approval': '#fbbf24',
  'Client Approval': '#eab308',
  Approved: '#14b8a6',
  Scheduled: '#6366f1',
  Published: '#22c55e',
  Cancelled: '#6b7280',
};

const PLATFORMS = ['Facebook', 'Instagram', 'TikTok', 'LinkedIn', 'X', 'YouTube', 'WhatsApp Channel'];
const CONTENT_TYPES = ['Post', 'Reel', 'Video', 'Story', 'Carousel', 'Cover', 'Ad Creative', 'Campaign Content'];
const NON_ACTIVE_STATUSES = ['Published', 'Cancelled'];

const parseTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const getDateString = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const getMonthStartEnd = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: getDateString(start), end: getDateString(end) };
};

const isOverdue = (task) => {
  if (NON_ACTIVE_STATUSES.includes(task.status)) return false;
  if (!task.dueDate) return false;
  return task.dueDate.getTime() < Date.now();
};

const SocialReportsPage = () => {
  const { language, toggleLanguage, isRTL } = useSocialLanguage();
  const t = TRANSLATIONS[language];
  const defaultRange = useMemo(() => getMonthStartEnd(), []);

  const [allTasks, setAllTasks] = useState([]);
  const [socialPages, setSocialPages] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [clientFilter, setClientFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [tasksSnap, pagesSnap, clientsSnap] = await Promise.all([
          getDocs(collection(db, 'contentTasks')),
          getDocs(collection(db, 'socialPages')),
          getDocs(query(collection(db, 'clients'), orderBy('name', 'asc'))),
        ]);

        const fetchedTasks = tasksSnap.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            dueDate: parseTimestamp(data.dueDate),
            scheduledDate: parseTimestamp(data.scheduledDate),
            publishedDate: parseTimestamp(data.publishedDate),
          };
        });
        setAllTasks(fetchedTasks);

        setSocialPages(pagesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setClients(clientsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError('Failed to load report data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const dateRange = useMemo(() => {
    if (!startDate || !endDate) return { start: null, end: null, isValid: false };
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T23:59:59.999`);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
      return { start: null, end: null, isValid: false };
    }
    return { start, end, isValid: true };
  }, [startDate, endDate]);

  // Tasks within the date range (by dueDate) and matching client/platform filters.
  const filteredTasks = useMemo(() => {
    if (!dateRange.isValid) return [];
    return allTasks.filter((task) => {
      if (clientFilter !== 'all' && task.clientId !== clientFilter) return false;
      if (platformFilter !== 'all' && !(task.platforms || [task.platform]).includes(platformFilter)) return false;
      if (!task.dueDate) return false;
      return task.dueDate >= dateRange.start && task.dueDate <= dateRange.end;
    });
  }, [allTasks, dateRange, clientFilter, platformFilter]);

  // Tasks published within the date range (by publishedDate), for the per-client published-vs-target metric.
  const publishedInRangeTasks = useMemo(() => {
    if (!dateRange.isValid) return [];
    return allTasks.filter((task) => {
      if (clientFilter !== 'all' && task.clientId !== clientFilter) return false;
      if (platformFilter !== 'all' && !(task.platforms || [task.platform]).includes(platformFilter)) return false;
      if (task.status !== 'Published' || !task.publishedDate) return false;
      return task.publishedDate >= dateRange.start && task.publishedDate <= dateRange.end;
    });
  }, [allTasks, dateRange, clientFilter, platformFilter]);

  const byStatus = useMemo(() => {
    const counts = {};
    STATUS_ORDER.forEach((s) => (counts[s] = 0));
    filteredTasks.forEach((task) => {
      if (counts[task.status] !== undefined) counts[task.status]++;
      else counts[task.status] = (counts[task.status] || 0) + 1;
    });
    const max = Math.max(1, ...Object.values(counts));
    return STATUS_ORDER.map((status) => ({ status, count: counts[status] || 0, max }));
  }, [filteredTasks]);

  const byClient = useMemo(() => {
    const clientNameMap = new Map(clients.map((c) => [c.id, c.name]));
    const targetsByClient = new Map();
    socialPages.forEach((page) => {
      const current = targetsByClient.get(page.clientId) || 0;
      targetsByClient.set(page.clientId, current + (Number(page.monthlyTarget) || 0));
    });

    const counts = new Map();
    filteredTasks.forEach((task) => {
      const key = task.clientId || 'unknown';
      const entry = counts.get(key) || { clientId: key, clientName: task.clientName || 'Unknown', count: 0 };
      entry.count++;
      counts.set(key, entry);
    });

    const publishedCounts = new Map();
    publishedInRangeTasks.forEach((task) => {
      const key = task.clientId || 'unknown';
      publishedCounts.set(key, (publishedCounts.get(key) || 0) + 1);
    });

    const rows = Array.from(counts.values()).map((entry) => ({
      ...entry,
      clientName: clientNameMap.get(entry.clientId) || entry.clientName,
      published: publishedCounts.get(entry.clientId) || 0,
      target: targetsByClient.get(entry.clientId) || 0,
    }));

    rows.sort((a, b) => b.count - a.count);
    const max = Math.max(1, ...rows.map((r) => r.count));
    return { rows, max };
  }, [filteredTasks, publishedInRangeTasks, clients, socialPages]);

  const byContentType = useMemo(() => {
    const counts = {};
    CONTENT_TYPES.forEach((t) => (counts[t] = 0));
    filteredTasks.forEach((task) => {
      if (counts[task.contentType] !== undefined) counts[task.contentType]++;
      else counts[task.contentType] = (counts[task.contentType] || 0) + 1;
    });
    const max = Math.max(1, ...Object.values(counts));
    return CONTENT_TYPES.map((type) => ({ type, count: counts[type] || 0, max }));
  }, [filteredTasks]);

  const byPlatform = useMemo(() => {
    const counts = {};
    PLATFORMS.forEach((p) => (counts[p] = 0));
    filteredTasks.forEach((task) => {
      (task.platforms || [task.platform]).forEach((p) => {
        if (counts[p] !== undefined) counts[p]++;
        else counts[p] = (counts[p] || 0) + 1;
      });
    });
    const max = Math.max(1, ...Object.values(counts));
    return PLATFORMS.map((platform) => ({ platform, count: counts[platform] || 0, max }));
  }, [filteredTasks]);

  const overdueTasks = useMemo(() => {
    return filteredTasks.filter(isOverdue).sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
  }, [filteredTasks]);

  const renderBarRows = (rows, labelKey, countKey = 'count', translateLabel = (v) => v) => (
    <div className={styles.barRows}>
      {rows.map((row) => {
        const widthPct = row.max > 0 ? Math.round((row[countKey] / row.max) * 100) : 0;
        const color = STATUS_COLORS[row[labelKey]] || 'var(--primary-color, #4a90e2)';
        return (
          <div key={row[labelKey]} className={styles.barRow}>
            <span className={styles.barLabel}>{translateLabel(row[labelKey])}</span>
            <div className={styles.barTrack}>
              <div
                className={styles.barFill}
                style={{ width: `${widthPct}%`, backgroundColor: color }}
              />
            </div>
            <span className={styles.barCount}>{row[countKey]}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.header}>
        <h2 className={styles.pageTitle}>{t.pageTitle}</h2>
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      <div className={styles.filtersContainer}>
        <div className={styles.filterGroup}>
          <label htmlFor="start-date">{t.startDate}</label>
          <input
            id="start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={styles.dateInput}
            max={endDate || undefined}
          />
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="end-date">{t.endDate}</label>
          <input
            id="end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={styles.dateInput}
            min={startDate || undefined}
          />
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="client-filter">{t.client}</label>
          <select
            id="client-filter"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">{t.allClients}</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="platform-filter">{t.platform}</label>
          <select
            id="platform-filter"
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">{t.allPlatforms}</option>
            {PLATFORMS.map((platform) => (
              <option key={platform} value={platform}>
                {getPlatformLabel(platform, language)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <p className={styles.errorMessage}>{error}</p>}
      {!dateRange.isValid && (
        <p className={styles.errorMessage}>{t.dateRangeError}</p>
      )}

      {loading ? (
        <div className={styles.loadingIndicator}>{t.loading}</div>
      ) : (
        dateRange.isValid && (
          <>
            <div className={styles.reportGrid}>
              <section className={styles.reportCard}>
                <h3 className={styles.cardTitle}>{t.byStatus}</h3>
                {renderBarRows(byStatus, 'status', 'count', (v) => getStatusLabel(v, language))}
              </section>

              <section className={styles.reportCard}>
                <h3 className={styles.cardTitle}>{t.byContentType}</h3>
                {renderBarRows(byContentType, 'type', 'count', (v) => getContentTypeLabel(v, language))}
              </section>

              <section className={styles.reportCard}>
                <h3 className={styles.cardTitle}>{t.byPlatform}</h3>
                {renderBarRows(byPlatform, 'platform', 'count', (v) => getPlatformLabel(v, language))}
              </section>

              <section className={`${styles.reportCard} ${styles.overdueCard}`}>
                <h3 className={styles.cardTitle}>
                  {t.overdueTasks} <span className={styles.countBadge}>{overdueTasks.length}</span>
                </h3>
                {overdueTasks.length === 0 ? (
                  <p className={styles.noDataMessage}>{t.noOverdueInRange}</p>
                ) : (
                  <ul className={styles.overdueList}>
                    {overdueTasks.slice(0, 10).map((task) => (
                      <li key={task.id} className={styles.overdueItem}>
                        <Link to={`/social/tasks/${task.id}`} className={styles.overdueLink}>
                          <span className={styles.overdueTitle}>{task.title || t.untitled}</span>
                          <span className={styles.overdueClient}>{task.clientName || t.noClient}</span>
                          <span className={styles.overdueDue}>{t.due} {formatDate(task.dueDate)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <section className={styles.byClientSection}>
              <h3 className={styles.cardTitle}>{t.byClient}</h3>
              {byClient.rows.length === 0 ? (
                <p className={styles.noDataMessage}>{t.noTasksInRange}</p>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t.colClient}</th>
                        <th>{t.colTasksDueInRange}</th>
                        <th>{t.colPublishedInRange}</th>
                        <th>{t.colMonthlyTarget}</th>
                        <th>{t.colProgress}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byClient.rows.map((row) => {
                        const progressPct =
                          row.target > 0 ? Math.min(100, Math.round((row.published / row.target) * 100)) : 0;
                        return (
                          <tr key={row.clientId}>
                            <td>{row.clientName}</td>
                            <td>{row.count}</td>
                            <td>{row.published}</td>
                            <td>{row.target}</td>
                            <td>
                              {row.target > 0 ? (
                                <div className={styles.progressWrap}>
                                  <div className={styles.progressTrack}>
                                    <div
                                      className={styles.progressFill}
                                      style={{ width: `${progressPct}%` }}
                                    />
                                  </div>
                                  <span className={styles.progressLabel}>
                                    {row.published} / {row.target}
                                  </span>
                                </div>
                              ) : (
                                <span className={styles.noTargetLabel}>{t.noTargetSet}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )
      )}
    </div>
  );
};

export default SocialReportsPage;
